import React, { FC } from 'react';
import _ from '@dh-fe/translate';
import classnames from 'classnames';
import { useTheme } from 'contexts/ThemeContext';
import OranumCoin from './assets/coin-oranum.svg?svgr';
import { abbreviateNumber, formatNumber } from './FormattedMoney.utils';
import styles from './FormattedMoney.module.scss';

export type FormatMoneyCurrency = 'credit' | 'coin' | 'usd';

interface FormattedMoneyProps {
    amount: number;
    currency: FormatMoneyCurrency;
    precision?: number;
    abbreviateAmount?: boolean;
    showNumberOnly?: boolean;
    iconWrapperClassName?: string;
    iconClassName?: string;
    showFreeValue?: boolean;
    showDashWhenZero?: boolean;
    showPlural?: boolean;
}

const FormattedMoney: FC<FormattedMoneyProps> = ({
    amount,
    currency,
    precision,
    abbreviateAmount = false,
    showNumberOnly = false,
    iconWrapperClassName = undefined,
    iconClassName = undefined,
    showFreeValue = false,
    showDashWhenZero = false,
    showPlural = true,
}) => {
    const { isOranum } = useTheme();
    const isNegative = amount < 0;
    const formattedPrice = abbreviateAmount
        ? abbreviateNumber(Math.abs(amount))
        : formatNumber(Math.abs(amount), { precision });
    const price = showDashWhenZero && amount === 0 ? '-' : formattedPrice;

    if (showFreeValue && amount === 0) return <>{_`Free`}</>;
    if (showNumberOnly) return <>{price}</>;

    if (currency === 'coin') {
        const iconWrapperClasses = classnames(styles.iconWrapper, iconWrapperClassName);
        const iconClasses = classnames(styles.icon, iconClassName);

        if (isOranum) {
            return (
                <span className={iconWrapperClasses}>
                    <OranumCoin aria-label="oranum coin icon" className={iconClasses} /> {price}
                </span>
            );
        }

        return <>{price}</>;
    }

    if (currency === 'credit') {
        let currency = _`credit`;
        if (showPlural) currency = amount === 1 ? _`credit` : _`credits`;
        return (
            <>
                {price} {currency}
            </>
        );
    }

    if (isNegative) {
        return <>-${price}</>;
    }

    return <>${price}</>;
};

export default FormattedMoney;
